import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { fetchCompanyUsers, formatUserLabel } from '../utils/companyUsers';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { canClaimOrReturnHandout, isCompanyAdmin } from '../utils/planAccess';
import { useConfirm } from '../context/ConfirmContext';
import './Tasks.css';
import './Handouts.css';

const UNASSIGNED_KEY = '__unassigned__';

const getAssigneeGroupKey = (handout, t) => {
  if (!handout.assigned_to) {
    return UNASSIGNED_KEY;
  }
  const name = `${handout.assignee_first_name || ''} ${handout.assignee_last_name || ''}`.trim();
  return name || handout.assignee_email || t('handouts:userFallback', { id: handout.assigned_to });
};

const getAssigneeLabel = (handout, t) => {
  if (!handout.assigned_to) {
    return null;
  }
  const name = `${handout.assignee_first_name || ''} ${handout.assignee_last_name || ''}`.trim();
  return name || handout.assignee_email || t('handouts:userFallback', { id: handout.assigned_to });
};

const groupHandouts = (handouts, t) => {
  const groups = {};
  handouts.forEach((handout) => {
    const key = getAssigneeGroupKey(handout, t);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(handout);
  });

  const assigneeKeys = Object.keys(groups)
    .filter((key) => key !== UNASSIGNED_KEY)
    .sort((a, b) => a.localeCompare(b));

  const orderedKeys = groups[UNASSIGNED_KEY] ? [...assigneeKeys, UNASSIGNED_KEY] : assigneeKeys;

  return orderedKeys.map((key) => ({
    key,
    label: key === UNASSIGNED_KEY ? t('common:unassigned') : key,
    items: groups[key].sort((a, b) => a.name.localeCompare(b.name))
  }));
};

const Handouts = () => {
  const { t } = useTranslation(['handouts', 'tasks', 'common']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [handouts, setHandouts] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHandout, setEditingHandout] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', assignedTo: '', locationId: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionHandoutId, setActionHandoutId] = useState(null);

  const companyId = user?.active_company_id || user?.company_id;
  const canManage = isCompanyAdmin(user);
  const canClaimReturn = canClaimOrReturnHandout(user);

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [handoutsRes, users, locationsRes] = await Promise.all([
        axios.get('/api/handouts'),
        canManage
          ? fetchCompanyUsers(companyId, user)
          : Promise.resolve([]),
        canManage
          ? axios.get('/api/locations').catch(() => ({ data: { data: [] } }))
          : Promise.resolve({ data: { data: [] } })
      ]);
      setHandouts(handoutsRes.data.data || []);
      setCompanyUsers(users);
      setLocations(locationsRes.data.data || []);
    } catch (err) {
      console.error('Error fetching handouts:', err);
      setError(formatError(err) || t('handouts:failedFetch'));
    } finally {
      setLoading(false);
    }
  };

  const groupedHandouts = useMemo(() => groupHandouts(handouts, t), [handouts, t]);

  const handleOpenCreate = () => {
    if (!canManage) {
      return;
    }
    setEditingHandout(null);
    setFormData({ name: '', description: '', assignedTo: '', locationId: '' });
    setError('');
    setShowModal(true);
  };

  const handleOpenEdit = (handout) => {
    if (!canManage) {
      return;
    }
    setEditingHandout(handout);
    setFormData({
      name: handout.name || '',
      description: handout.description || '',
      assignedTo: handout.assigned_to ? String(handout.assigned_to) : '',
      locationId: handout.location_id ? String(handout.location_id) : ''
    });
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHandout(null);
    setFormData({ name: '', description: '', assignedTo: '', locationId: '' });
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError('');

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      assignedTo: formData.assignedTo ? parseInt(formData.assignedTo, 10) : null,
      locationId: formData.locationId ? parseInt(formData.locationId, 10) : null
    };

    try {
      if (editingHandout) {
        await axios.put(`/api/handouts/${editingHandout.id}`, payload);
      } else {
        await axios.post('/api/handouts', payload);
      }
      handleCloseModal();
      await fetchData();
    } catch (err) {
      setError(formatError(err) || t('handouts:failedSave'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingHandout) {
      return;
    }
    if (!(await confirm(t('handouts:confirmDelete', { name: editingHandout.name }), { danger: true }))) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axios.delete(`/api/handouts/${editingHandout.id}`);
      handleCloseModal();
      await fetchData();
    } catch (err) {
      setError(formatError(err) || t('handouts:failedDelete'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClaim = async (handout, e) => {
    e.stopPropagation();
    if (actionHandoutId) {
      return;
    }
    setActionHandoutId(handout.id);
    try {
      await axios.post(`/api/handouts/${handout.id}/claim`);
      await fetchData();
    } catch (err) {
      setError(formatError(err) || t('tasks:failedClaimHandout'));
    } finally {
      setActionHandoutId(null);
    }
  };

  const handleReturn = async (handout, e) => {
    e.stopPropagation();
    if (actionHandoutId) {
      return;
    }
    setActionHandoutId(handout.id);
    try {
      await axios.post(`/api/handouts/${handout.id}/return`);
      await fetchData();
    } catch (err) {
      setError(formatError(err) || t('tasks:failedReturnHandout'));
    } finally {
      setActionHandoutId(null);
    }
  };

  const renderHandoutActions = (handout) => {
    if (!canClaimReturn) {
      const holder = getAssigneeLabel(handout, t);
      return holder
        ? <span className="handout-status-held">{holder}</span>
        : <span className="handout-status-unclaimed">{t('tasks:handoutUnclaimed')}</span>;
    }

    if (!handout.assigned_to) {
      return (
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={actionHandoutId === handout.id}
          onClick={(e) => handleClaim(handout, e)}
        >
          {t('tasks:handoutClaim')}
        </button>
      );
    }

    if (handout.assigned_to === user.id) {
      return (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={actionHandoutId === handout.id}
          onClick={(e) => handleReturn(handout, e)}
        >
          {t('tasks:handoutReturn')}
        </button>
      );
    }

    return <span className="handout-status-held">{getAssigneeLabel(handout, t)}</span>;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="handouts-page">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('handouts:pageTitle')}</h1>
          {canManage && (
            <button type="button" className="btn btn-primary tasks-header-create-btn" onClick={handleOpenCreate}>
              <span className="material-symbols-outlined" aria-hidden="true">add</span>
              {t('handouts:createHandout')}
            </button>
          )}
        </div>

        {error && !showModal && <div className="error">{error}</div>}

        {groupedHandouts.length > 0 ? (
          groupedHandouts.map((group, index) => (
            <React.Fragment key={group.key}>
              {index > 0 && <hr className="tasks-section-divider" aria-hidden="true" />}
              <section className="tasks-section">
                <div className="tasks-section-header">
                  <h2 className="tasks-section-heading">{group.label}</h2>
                </div>
                <div className="tasks-list-table-wrap tasks-list-table-desktop">
                  <table className="table tasks-list-table">
                    <thead>
                      <tr>
                        <th>{t('common:name')}</th>
                        <th>{t('handouts:description')}</th>
                        <th>{t('handouts:columnLocation')}</th>
                        <th>{t('common:status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((handout) => (
                        <tr
                          key={handout.id}
                          className={`tasks-list-table-row${canManage ? ' tasks-list-table-row--interactive' : ''}`}
                          onClick={canManage ? () => handleOpenEdit(handout) : undefined}
                        >
                          <td className="tasks-list-table-title">{handout.name}</td>
                          <td>{handout.description || t('tasks:emptyCell')}</td>
                          <td>{handout.location_name || t('tasks:emptyCell')}</td>
                          <td className="handout-actions-cell" onClick={(e) => e.stopPropagation()}>
                            {renderHandoutActions(handout)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="tasks-card-grid tasks-list-cards-mobile">
                  {group.items.map((handout) => (
                    <article
                      key={handout.id}
                      className={`tasks-redesign-card${canManage ? ' tasks-redesign-card--interactive' : ''}`}
                      onClick={canManage ? () => handleOpenEdit(handout) : undefined}
                    >
                      <div className="tasks-redesign-card-body">
                        <div className="tasks-redesign-card-top">
                          <h3 className="tasks-redesign-card-title">{handout.name}</h3>
                        </div>
                        <div className="tasks-redesign-card-meta">
                          {handout.description && (
                            <span className="tasks-redesign-meta-item">
                              <span className="material-symbols-outlined" aria-hidden="true">notes</span>
                              {handout.description}
                            </span>
                          )}
                          <span className="tasks-redesign-meta-item">
                            <span className="material-symbols-outlined" aria-hidden="true">location_on</span>
                            {handout.location_name || t('handouts:companyLevel')}
                          </span>
                        </div>
                        <div
                          className="tasks-redesign-card-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderHandoutActions(handout)}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            </React.Fragment>
          ))
        ) : (
          <EmptyState
            icon="key"
            title={t('handouts:noHandoutsTitle')}
            description={
              canManage
                ? t('handouts:noHandoutsDescription')
                : t('handouts:noHandoutsDescriptionMember')
            }
            actionLabel={canManage ? t('handouts:createHandout') : undefined}
            onAction={canManage ? handleOpenCreate : undefined}
          />
        )}

        <Modal
          isOpen={canManage && showModal}
          onClose={handleCloseModal}
          title={editingHandout ? t('handouts:editHandout') : t('handouts:createHandout')}
        >
          {error && <div className="error">{error}</div>}
          <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="handout-name">{t('common:name')} *</label>
                  <input
                    id="handout-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="handout-description">{t('tasks:descriptionLabel')}</label>
                  <textarea
                    id="handout-description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="handout-location">{t('tasks:locationLabel')}</label>
                  <select
                    id="handout-location"
                    name="locationId"
                    value={formData.locationId}
                    onChange={handleChange}
                  >
                    <option value="">{t('handouts:noLocationCompanyLevel')}</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="handout-assigned-to">{t('handouts:assignedToLabel')}</label>
                  <select
                    id="handout-assigned-to"
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleChange}
                  >
                    <option value="">{t('common:unassigned')}</option>
                    {companyUsers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {formatUserLabel(member)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="modal-actions">
                  {editingHandout && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={submitting}
                    >
                      {t('common:delete')}
                    </button>
                  )}
                  <div className="modal-actions-right">
                    <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                      {t('common:cancel')}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting
                        ? t('common:saving')
                        : editingHandout
                          ? t('common:save')
                          : t('common:create')}
                    </button>
                  </div>
                </div>
              </form>
        </Modal>
      </div>
    </div>
  );
};

export default Handouts;
